import { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays, MapPin, Package, RefreshCw, Search, Truck, X } from 'lucide-react';
import { apiUrl } from '../../shared/api/apiConfig';
import { useAuth } from '../../shared/auth/AuthContext';
import { getUserAvatarLabel } from '../../shared/auth/avatarLabel';
import { getUserDisplayName } from '../../shared/auth/userDisplayName';
import { KioskHero } from '../weighing/kiosk/KioskHero';
import { LoadDetailModal } from '../weighing/kiosk/LoadDetailModal';
import {
  fetchRequestLoadDetail,
  fetchRequestLoadSummaries,
  updateRequestLoadStatus,
  voidWeighingRequest,
} from '../weighing/kiosk/kioskApi';
import {
  createRequestLoadSummaryState,
  isCompletedRequest,
} from '../weighing/kiosk/kioskTransforms';
import { useKioskHub } from '../weighing/useKioskHub';
import '../weighing/kiosk/KioskPage.css';
import './TpnBookingsPage.css';

const STATUS_LABELS = {
  1: 'รอจัดรถ',
  2: 'จัดรถแล้ว',
  3: 'ยกเลิก',
  4: 'ขึ้นรถแล้ว',
  5: 'กำลังขึ้นของ',
};

const PENDING_ASSIGNMENT_STATUS = 1;
const ASSIGNED_STATUS = 2;
const LOADED_STATUS = 4;
const LOADING_IN_PROGRESS_STATUS = 5;
const AWAITING_OR_ASSIGNED_FILTER = 'awaiting-or-assigned';
const LOAD_CREATABLE_BOOKING_STATUSES = new Set([PENDING_ASSIGNMENT_STATUS, ASSIGNED_STATUS]);
const BOOKING_PAGE_SIZE = 12;

const SLOT_LABELS = {
  1: 'Morning',
  2: 'Afternoon',
};

function formatDate(value) {
  if (!value) {
    return '-';
  }

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).format(new Date(value));
}

function toDateInputValue(value) {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function formatNumber(value, digits = 0) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return '0';
  }

  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(number);
}

function getBookingItemWeight(item) {
  const weight = item?.calculatedGoodsWeightKg ?? item?.totalWeight;
  return Number(weight) || 0;
}

function getBookingTotalWeight(booking) {
  return (booking.items ?? []).reduce((total, item) => total + getBookingItemWeight(item), 0);
}

function getBookingTotalPacks(booking) {
  return (booking.items ?? []).reduce((total, item) => total + (Number(item.quantity) || 0), 0);
}

function getStatusLabel(status) {
  return STATUS_LABELS[status] ?? `Status ${status}`;
}

function getLoadWeightProgress(loadWeight, bookingWeight) {
  const actual = Number(loadWeight) || 0;
  const target = Number(bookingWeight) || 0;

  if (target <= 0) {
    return {
      barPercent: 0,
      displayPercent: 0,
      isOver: false,
      weightLabel: `${formatNumber(actual, 2)} kg`,
    };
  }

  const rawPercent = (actual / target) * 100;

  return {
    barPercent: Math.min(Math.max(rawPercent, 0), 100),
    displayPercent: Math.round(rawPercent),
    isOver: actual > target,
    weightLabel: `${formatNumber(actual, 2)} / ${formatNumber(target, 2)} kg`,
  };
}

function getBookingKey(booking) {
  return String(booking.id ?? booking.bookingNumber);
}

function isCancelledLoad(load) {
  return String(load?.status ?? '').toLowerCase() === 'cancelled';
}

function buildLoadCardState(load) {
  if (!load?.loadId) {
    return null;
  }

  if (isCancelledLoad(load)) {
    return {
      type: 'success',
      loadId: load.loadId,
      name: load.name,
      status: load.status,
      existing: true,
      requestCount: 0,
      totalWeight: 0,
      bookedWeight: 0,
    };
  }

  const aggregate = load.aggregate ?? {};

  return {
    type: 'success',
    loadId: load.loadId,
    name: load.name,
    status: load.status ?? null,
    existing: true,
    requestCount: Number(aggregate.completedRequestCount) || 0,
    totalWeight: Number(aggregate.scannedWeightKg) || 0,
    bookedWeight: Number(aggregate.bookedWeightKg) || 0,
    bookedPacks: Number(aggregate.bookedPacks) || 0,
    scannedPacks: Number(aggregate.scannedPacks) || 0,
    products: Array.isArray(aggregate.products) ? aggregate.products : [],
  };
}

function ProductLines({ items }) {
  if (!items?.length) {
    return <span className="tpn-bookings-muted">No products</span>;
  }

  return (
    <div className="tpn-bookings-products">
      {items.map((item) => (
        <div key={item.id} className="tpn-bookings-product-line">
          <div>
            <strong>{item.productName || 'Unnamed product'}</strong>
          </div>
          <div className="tpn-bookings-product-metrics">
            <span>{formatNumber(item.quantity)} packs</span>
            <span>{formatNumber(getBookingItemWeight(item), 2)} kg</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function AssignedCarDetail({ booking }) {
  const vehicle = booking.assignedVehicle;

 /* if (vehicle) {
    return (
      <div className="tpn-bookings-car-card">
        <div className="tpn-bookings-car-head">
          <span className="tpn-bookings-car-plate">
            <Truck size={14} aria-hidden="true" />
            {vehicle.plateNumber || booking.manualPlateNumber || 'No plate'}
          </span>
          <span className={`tpn-bookings-car-state ${vehicle.isActive ? 'active' : 'inactive'}`}>
            {vehicle.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
        <dl className="tpn-bookings-car-details">
          <div>
            <dt>ขนาดบรรทุก</dt>
            <dd>{vehicle.loadingCapacity ? `${formatNumber(vehicle.loadingCapacity, 2)} kg` : '-'}</dd>
          </div>
          <div>
            <dt>คนขับ</dt>
            <dd>{booking.driverName || vehicle.defaultDriver || '-'}</dd>
          </div>
        </dl>
      </div>
    );
  }
*/

  if (booking.manualPlateNumber) {
    let vehiclePlate = '';
    let vehicleType = booking.vehicleType || '';
    if(booking.manualPlateNumber.trim()) {
      let vehicleDetails = booking.manualPlateNumber.split(' ');
      vehiclePlate = vehicleDetails[0];
    }
      return (
      <div className="tpn-bookings-car-card manual">
        <div className="tpn-bookings-car-head">
          <span className="tpn-bookings-car-plate">
            <Truck size={14} aria-hidden="true" />
            {vehiclePlate || 'ไม่ระบุทะเบียนรถ'} {vehicleType ? `/${vehicleType}` : ''}
          </span>
        </div>
        <dl className="tpn-bookings-car-details">
          <div>
            <dt>คนขับ</dt>
            <dd>{booking.driverName || '-'}</dd>
          </div>
        </dl>
      </div>
    );
  }

  return (
    <div className="tpn-bookings-car-empty">
      <Truck size={14} aria-hidden="true" />
      <span>ยังไม่จองรถ</span>
      {booking.driverName && <small>{booking.driverName}</small>}
    </div>
  );
}

export default function TpnBookingsPage() {
  const { user } = useAuth();
  const resolvedActorName = getUserDisplayName(user);
  const actorName = resolvedActorName || 'bookings-page';
  const actorSource = resolvedActorName ? `bookings:${resolvedActorName}` : 'bookings-page';
  const avatarLabel = getUserAvatarLabel(user);
  const [bookings, setBookings] = useState([]);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(AWAITING_OR_ASSIGNED_FILTER);
  const [loadingDateFilter, setLoadingDateFilter] = useState(() => toDateInputValue(new Date()));
  const [loadState, setLoadState] = useState({ type: 'idle', message: '' });
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [linkedLoadsByBookingId, setLinkedLoadsByBookingId] = useState({});
  const [createLoadStateByBookingId, setCreateLoadStateByBookingId] = useState({});
  const [detailLoadId, setDetailLoadId] = useState('');
  const [detailLoad, setDetailLoad] = useState(null);
  const [detailLoadState, setDetailLoadState] = useState({ type: 'idle', message: '' });
  const [detailLoadSummary, setDetailLoadSummary] = useState(() => createRequestLoadSummaryState());
  const [source, setSource] = useState(null);
  const tableWrapRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);
  const loadMoreStateRef = useRef(null);
  const bookingRequestIdRef = useRef(0);
  const searchDebounceRef = useRef(null);

  loadMoreStateRef.current = {
    hasMore,
    isLoadingMore,
    isPending: loadState.type === 'pending',
    loadingDateFilter,
    page,
    query,
    statusFilter,
  };

  async function mergeLinkedLoad(load) {
    if (!load?.tpnBookingId) {
      return;
    }

    const bookingKey = String(load.tpnBookingId);
    if (isCancelledLoad(load)) {
      setLinkedLoadsByBookingId((prev) => ({
        ...prev,
        [bookingKey]: (prev[bookingKey] ?? []).filter((item) => item.loadId !== load.loadId),
      }));
    } else {
      await loadLinkedLoads([bookingKey]);
    }
    setCreateLoadStateByBookingId((prev) => {
      if (!(bookingKey in prev)) {
        return prev;
      }

      const { [bookingKey]: _removed, ...next } = prev;
      return next;
    });

    setDetailLoad((current) => (
      current?.loadId === load.loadId ? { ...current, ...load } : current
    ));
  }

  async function loadBookings({
    loadingDate = loadingDateFilter,
    bookingStatus = statusFilter,
    bookingSearch = query,
    requestedPage = 1,
    append = false,
    forceRefresh = false,
  } = {}) {
    const requestId = ++bookingRequestIdRef.current;
    if (append) {
      setIsLoadingMore(true);
    } else {
      setLoadState({ type: 'pending', message: 'Loading TPN bookings...' });
      setHasMore(false);
    }

    try {
      const sourceStatuses = bookingStatus === 'all'
        ? [String(LOADED_STATUS), null]
        : [bookingStatus === String(LOADED_STATUS) ? String(LOADED_STATUS) : null];
      const payloads = await Promise.all(sourceStatuses.map(async (sourceStatus) => {
        const params = new URLSearchParams({
          page: String(requestedPage),
          pageSize: String(BOOKING_PAGE_SIZE),
        });
        if (loadingDate) {
          params.set('loadingDate', loadingDate);
        }
        if (sourceStatus) {
          params.set('status', sourceStatus);
        }
        if (bookingSearch.trim()) {
          params.set('search', bookingSearch.trim());
        }
        if (forceRefresh) {
          params.set('forceRefresh', 'true');
        }

        const response = await fetch(
          apiUrl(`/tpnbookings?${params}`),
          forceRefresh ? { cache: 'no-store' } : undefined,
        );
        const payload = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(payload?.message || 'Failed to load TPN bookings.');
        }

        return payload;
      }));

      if (requestId !== bookingRequestIdRef.current) {
        return;
      }

      const nextBookingsById = new Map();
      payloads.forEach((payload) => {
        const sourceBookings = Array.isArray(payload?.bookings) ? payload.bookings : [];
        sourceBookings.forEach((booking) => {
          const bookingKey = getBookingKey(booking);
          if (!nextBookingsById.has(bookingKey)) {
            nextBookingsById.set(bookingKey, booking);
          }
        });
      });
      const nextBookings = [...nextBookingsById.values()];
      const sourcePayload = payloads[0];

      setSource({
        host: sourcePayload?.sourceHost ?? 'TPN API',
        retrievedAt: sourcePayload?.retrievedAt ?? null,
      });

      setBookings((current) => {
        if (!append) {
          return nextBookings;
        }

        const existingIds = new Set(current.map((booking) => getBookingKey(booking)));
        return [...current, ...nextBookings.filter((booking) => !existingIds.has(getBookingKey(booking)))];
      });
      void loadLinkedLoads(
        nextBookings.map((booking) => getBookingKey(booking)),
        { replace: !append },
      );
      setPage(requestedPage);
      setHasMore(payloads.some((payload) => {
        const sourceBookings = Array.isArray(payload?.bookings) ? payload.bookings : [];
        return typeof payload?.hasMore === 'boolean'
          ? payload.hasMore
          : sourceBookings.length === BOOKING_PAGE_SIZE;
      }));
      setLoadState({ type: 'success', message: '' });
    } catch (error) {
      if (requestId !== bookingRequestIdRef.current) {
        return;
      }
      setLoadState({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to load TPN bookings.',
      });
    } finally {
      if (requestId === bookingRequestIdRef.current) {
        setIsLoadingMore(false);
      }
    }
  }

  function handleLoadingDateFilterChange(event) {
    clearTimeout(searchDebounceRef.current);
    const nextDate = event.target.value;
    setLoadingDateFilter(nextDate);
    void loadBookings({ loadingDate: nextDate, bookingStatus: statusFilter });
  }

  function clearLoadingDateFilter() {
    clearTimeout(searchDebounceRef.current);
    setLoadingDateFilter('');
    void loadBookings({ loadingDate: '', bookingStatus: statusFilter });
  }

  function handleStatusFilterChange(nextStatus) {
    if (nextStatus === statusFilter) {
      return;
    }

    clearTimeout(searchDebounceRef.current);
    setStatusFilter(nextStatus);
    void loadBookings({ loadingDate: loadingDateFilter, bookingStatus: nextStatus });
  }

  useEffect(() => {
    void loadBookings();
    return () => clearTimeout(searchDebounceRef.current);
  }, []);

  function handleSearchChange(event) {
    const nextQuery = event.target.value;
    setQuery(nextQuery);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      void loadBookings({
        loadingDate: loadingDateFilter,
        bookingStatus: statusFilter,
        bookingSearch: nextQuery,
      });
    }, 300);
  }

  useKioskHub({
    onLoadUpdated: mergeLinkedLoad,
  });

  async function refreshData() {
    await loadBookings({
      loadingDate: loadingDateFilter,
      bookingStatus: statusFilter,
      forceRefresh: true,
    });
  }

  useEffect(() => {
    const scrollRoot = tableWrapRef.current;
    const sentinel = loadMoreSentinelRef.current;
    if (!scrollRoot || !sentinel || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) {
        return;
      }

      const current = loadMoreStateRef.current;
      if (!current?.hasMore || current.isLoadingMore || current.isPending) {
        return;
      }

      current.isLoadingMore = true;
      void loadBookings({
        loadingDate: current.loadingDateFilter,
        bookingStatus: current.statusFilter,
        bookingSearch: current.query,
        requestedPage: current.page + 1,
        append: true,
      });
    }, {
      root: scrollRoot,
      rootMargin: '0px 0px 300px 0px',
    });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  async function loadLinkedLoads(bookingIds, { replace = false } = {}) {
    const uniqueBookingIds = [...new Set((bookingIds ?? []).filter(Boolean).map(String))];
    if (uniqueBookingIds.length === 0) {
      if (replace) {
        setLinkedLoadsByBookingId({});
      }
      return;
    }

    try {
      const data = await fetchRequestLoadSummaries({ tpnBookingIds: uniqueBookingIds });

      const activeLoads = data.filter((load) => load?.tpnBookingId != null && !isCancelledLoad(load));
      const entries = activeLoads.map((load) => {
        const loadCardState = buildLoadCardState(load);
        if (loadCardState) {
          return [String(load.tpnBookingId), loadCardState];
        }
        return null;
      });
      const linked = Object.fromEntries(uniqueBookingIds.map((id) => [id, []]));
      entries.filter(Boolean).forEach(([bookingId, loadCardState]) => {
        const bookingLoads = linked[bookingId] ?? [];
        if (!bookingLoads.some((item) => item.loadId === loadCardState.loadId)) {
          linked[bookingId] = [...bookingLoads, loadCardState];
        }
      });

      setLinkedLoadsByBookingId((current) => (replace ? linked : { ...current, ...linked }));
    } catch {
      // silent — existing state is unaffected
    }
  }

  async function createLoadFromBooking(booking) {
    if (!LOAD_CREATABLE_BOOKING_STATUSES.has(booking.status)) {
      return;
    }

    const bookingId = booking.id != null ? String(booking.id) : '';
    if (!bookingId) {
      const bookingKey = getBookingKey(booking);
      setCreateLoadStateByBookingId((prev) => ({
        ...prev,
        [bookingKey]: { type: 'error', message: 'Missing booking ID.' },
      }));
      return;
    }

    const bookingKey = getBookingKey(booking);
    setCreateLoadStateByBookingId((prev) => ({ ...prev, [bookingKey]: { type: 'pending' } }));

    const productLines = (booking.items ?? []).map((item) => {
      const meta = [item.brandName, item.size].filter(Boolean).join(' ');
      const label = meta ? `${item.productName} (${meta})` : item.productName;
      return `- ${label} — ${formatNumber(item.quantity)} packs — ${formatNumber(getBookingItemWeight(item), 2)} kg`;
    });

    const noteLines = [
      booking.customerName ? `Customer: ${booking.customerName}` : null,
      booking.deliveryLocation ? `Location: ${booking.deliveryLocation}` : null,
      booking.loadingDate ? `Loading: ${formatDate(booking.loadingDate)}${booking.slot ? ` (${SLOT_LABELS[booking.slot] ?? `Slot ${booking.slot}`})` : ''}` : null,
      productLines.length > 0 ? `\nProducts:\n${productLines.join('\n')}` : null,
    ].filter(Boolean).join('\n');

    try {
      const response = await fetch(apiUrl('/requestloads'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${booking.bookingNumber || booking.id} - ${booking.customerName || 'Unnamed'}`,
          direction: 'Outbound',
          notes: noteLines || null,
          createdBy: actorName,
          tpnBookingId: bookingId,
          bookedWeightKg: getBookingTotalWeight(booking) || null,
          bookedItems: (booking.items ?? []).map((item) => ({
            canonicalProductId: item.canonicalProductId ?? null,
            canonicalProductName: item.canonicalProductName ?? null,
            palletTypeId: item.palletTypeId ?? null,
            palletTypeName: item.palletTypeName ?? null,
            capacityProfileId: item.capacityProfileId ?? null,
            capacityLabel: item.capacityLabel ?? null,
            productName: item.productName ?? null,
            brandName: item.brandName ?? null,
            size: item.size ?? null,
            quantityPacks: Number(item.quantityPacks ?? item.quantity) || 0,
            totalWeight: getBookingItemWeight(item) || null,
            calculatedGoodsWeightKg: item.calculatedGoodsWeightKg != null
              ? Number(item.calculatedGoodsWeightKg)
              : null,
            weightPerPack: item.weightPerPack != null ? Number(item.weightPerPack) : null,
            packagePerPallet: item.packagePerPallet != null || item.packagePerPalette != null
              ? Number(item.packagePerPallet ?? item.packagePerPalette)
              : null,
            palletQuantity: item.palletQuantity != null ? Number(item.palletQuantity) : null,
            unitPrice: item.unitPrice != null ? Number(item.unitPrice) : null,
          })),
        }),
      });

      const payload = await response.json().catch(() => null);

      if (response.status === 409 && payload?.loadId) {
        // A load already exists for this booking — treat as success.
        setCreateLoadStateByBookingId((prev) => {
          const { [bookingKey]: _removed, ...next } = prev;
          return next;
        });
        await loadLinkedLoads([bookingId]);
        return;
      }

      if (!response.ok) {
        throw new Error(payload?.message || `ไม่สามารถสร้างโหลดได้ (${response.status})`);
      }

      setBookings((current) => current.map((currentBooking) => (
        String(currentBooking?.id ?? '') === bookingId
          ? { ...currentBooking, status: LOADING_IN_PROGRESS_STATUS }
          : currentBooking
      )));
      void loadBookings({
        loadingDate: loadingDateFilter,
        bookingStatus: statusFilter,
        bookingSearch: query,
        forceRefresh: true,
      });

      await loadLinkedLoads([bookingId]);
      setCreateLoadStateByBookingId((prev) => {
        const { [bookingKey]: _removed, ...next } = prev;
        return next;
      });
      alert(`สร้างโหลด ${booking.bookingNumber || ''} สำหรับ ${booking.customerName || ''} เรียบร้อยแล้ว คุณสามารถตรวจสอบรายละเอียดโหลดได้ที่หน้าจอกำลังขึ้นของ หรือหน้าจอการชั่งน้ำหนัก`);
    } catch (error) {
      setCreateLoadStateByBookingId((prev) => ({
        ...prev,
        [bookingKey]: { type: 'error', message: error instanceof Error ? error.message : 'ไม่สามารถสร้างโหลดได้' },
      }));
    }

  }

  async function openLoadDetail(loadId) {
    setDetailLoadId(loadId);
    setDetailLoad(null);
    setDetailLoadState({ type: 'pending', message: 'Loading load detail...' });
    setDetailLoadSummary(createRequestLoadSummaryState({ status: 'pending' }));

    try {
      const { detail, requests: relatedRequests } = await fetchRequestLoadDetail(loadId);
      setDetailLoad(detail);
      setDetailLoadSummary(createRequestLoadSummaryState({
        requests: relatedRequests,
        requestCount: relatedRequests.filter(isCompletedRequest).length,
        status: 'success',
      }));
      setDetailLoadState({ type: 'success', message: '' });
    } catch (error) {
      setDetailLoadState({
        type: 'error',
        message: error instanceof Error ? error.message : 'Unable to load detail.',
      });
    }
  }

  function closeLoadDetail() {
    setDetailLoadId('');
    setDetailLoad(null);
    setDetailLoadSummary(createRequestLoadSummaryState());
    setDetailLoadState({ type: 'idle', message: '' });
  }

  async function updateDetailLoadStatus(action) {
    if (!detailLoad?.loadId) return;
    setDetailLoadState({ type: 'pending', message: `${action === 'complete' ? 'Completing' : 'Cancelling'} load...` });
    try {
      const updated = await updateRequestLoadStatus(detailLoad.loadId, action, actorName);
      setDetailLoad(updated);
      await mergeLinkedLoad(updated);
      setDetailLoadState({ type: 'success', message: `Load '${updated.loadId}' updated.` });
    } catch (error) {
      setDetailLoadState({
        type: 'error',
        message: error instanceof Error ? error.message : `Unable to ${action} the load.`,
      });
    }
  }

  async function voidDetailRequest(requestId) {
    setDetailLoadState({ type: 'pending', message: 'Voiding request...' });
    try {
      await voidWeighingRequest(requestId, { voidedBy: actorName, source: actorSource });
      await openLoadDetail(detailLoadId);
    } catch (error) {
      setDetailLoadState({
        type: 'error',
        message: error instanceof Error ? error.message : 'Unable to void the request.',
      });
    }
  }

  const filteredBookings = useMemo(
    () => bookings.filter((booking) => {
      const statusMatches = statusFilter === 'all'
        || (statusFilter === AWAITING_OR_ASSIGNED_FILTER
          ? LOAD_CREATABLE_BOOKING_STATUSES.has(booking.status)
          : String(booking.status) === statusFilter);
      const dateMatches = !loadingDateFilter || toDateInputValue(booking.loadingDate) === loadingDateFilter;
      return statusMatches && dateMatches;
    }),
    [bookings, loadingDateFilter, statusFilter]
  );

  const isLoading = loadState.type === 'pending';
  const isInitialLoading = isLoading && bookings.length === 0;
  const loadingMessage = isLoadingMore
    ? 'Loading more bookings...'
    : isLoading
      ? 'Loading bookings...'
      : '';

  return (
    <main className="tpn-bookings-page kiosk-page">
      <KioskHero
        eyebrow="เลือกรายการจอง"
        title="รายการจองทั้งหมด"
        avatarLabel={avatarLabel}
        rightContent={(
          <button
            type="button"
            className="tpn-bookings-refresh kiosk-secondary-btn"
            onClick={refreshData}
            disabled={isLoading}
          >
            <RefreshCw size={18} aria-hidden="true" />
            {isLoading ? 'Refreshing' : 'Refresh'}
          </button>
        )}
      />

      <section className="tpn-bookings-controls kiosk-card" aria-label="Booking filters">
        <label className="tpn-bookings-search">
          <Search size={18} aria-hidden="true" />
          <span className="sr-only">ค้นหารายการจอง</span>
          <input
            type="search"
            value={query}
            onChange={handleSearchChange}
            placeholder="ค้นหารายการจอง, ลูกค้า, สถานที่, สินค้า, คนขับ ฯลฯ"
          />
        </label>
        <div className="tpn-bookings-date-filter">
          <label htmlFor="tpn-bookings-loading-date">วันที่โหลด</label>
          <div className="tpn-bookings-date-input">
            <CalendarDays size={18} aria-hidden="true" />
            <input
              id="tpn-bookings-loading-date"
              type="date"
              value={loadingDateFilter}
              onChange={handleLoadingDateFilterChange}
            />
            {loadingDateFilter ? (
              <button
                type="button"
                className="tpn-bookings-clear-date"
                onClick={clearLoadingDateFilter}
                aria-label="ล้างตัวกรองวันที่โหลด"
              >
                <X size={16} aria-hidden="true" />
              </button>
            ) : null}
          </div>
        </div>
        <div className="tpn-bookings-status-tabs" role="group" aria-label="กรองตามสถานะ">
          {[
            ['all', 'ทั้งหมด'],
            [AWAITING_OR_ASSIGNED_FILTER, 'รอ/จัดรถแล้ว'],
           // ['1', 'รอจัดรถ'],
           // ['2', 'จัดรถแล้ว'],
           // ['3', 'ยกเลิก'],
            ['5', 'กำลังขึ้นของ'],
            ['4', 'ขึ้นรถแล้ว'],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={statusFilter === value ? 'active' : ''}
              onClick={() => handleStatusFilterChange(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </section>

      {loadState.type === 'error' && (
        <section className="tpn-bookings-alert" role="alert">
          {loadState.message}
        </section>
      )}

      <section className="tpn-bookings-results kiosk-card" >
        <div className="tpn-bookings-result-count">
          <div>จำนวนรายการ : {formatNumber(filteredBookings.length)}</div>
          <div>
            <strong>{source?.host ?? 'Waiting for connection'}</strong>
          </div>
          {loadingMessage ? (
            <div className="tpn-bookings-loading-status" role="status" aria-live="polite">
              <span className="tpn-bookings-loading-spinner" aria-hidden="true" />
              {loadingMessage}
            </div>
          ) : null}
        </div>
        <div ref={tableWrapRef} className="tpn-bookings-table-wrap">
          <table className="tpn-bookings-table">
            <thead>
              <tr>
                <th>ใบจอง</th>
                <th>ลูกค้า</th>
                <th>กำหนดการ</th>
                <th>สินค้า</th>
                <th>รวม</th>
                <th>การขนส่ง</th>
                <th>สถานะ</th>
                <th>เปิดโหลด</th>
              </tr>
            </thead>
            <tbody>
              {isInitialLoading && (
                <tr>
                  <td colSpan={8} className="tpn-bookings-empty">
                    Loading bookings...
                  </td>
                </tr>
              )}

              {!isInitialLoading && filteredBookings.map((booking) => {
                const bookingKey = getBookingKey(booking);
                const linkedLoads = linkedLoadsByBookingId[bookingKey] ?? [];
                const createState = createLoadStateByBookingId[bookingKey];
                const canCreateLoad = LOAD_CREATABLE_BOOKING_STATUSES.has(booking.status);
                const loadDate = new Date(booking.loadingDate);
                let formattedLoadDate;
                // Check if the date is valid
                if (!isNaN(loadDate.getTime())) {
                    formattedLoadDate = new Intl.DateTimeFormat('en-GB').format(loadDate);
                    console.log(formattedLoadDate);
                } else {
                    console.error("The date provided is invalid.");
                    formattedLoadDate = 'ไม่ระบุวันขึ้นของ';
                    // Fallback logic here (e.g., return an empty string or default text)
                }
                return (
                <tr key={booking.id}>
                  <td data-label="Booking">
                    <div className="tpn-bookings-primary">
                      <strong>{booking.bookingNumber || 'No number'}</strong>
                      {booking.createdBy ? <span>{booking.createdBy}</span> : null}
                    </div>
                  </td>
                  <td data-label="Customer">
                    <div className="tpn-bookings-location">
                      <strong>{booking.customerName || '-'}</strong>
                      <span>
                        <MapPin size={14} aria-hidden="true" />
                        {booking.deliveryLocation || '-'}
                      </span>
                    </div>
                  </td>
                  <td data-label="Schedule">
                    <div className="tpn-bookings-schedule">
                      {/*}
                      <span>
                        <CalendarDays size={14} aria-hidden="true" />
                        Book {formatDate(booking.bookingDate)}
                      </span>
                      */}

                      <span>{formattedLoadDate == undefined ? 'ไม่ระบุวันขึ้นของ' : formattedLoadDate}</span>
                      {Number(booking.slot) > 0 ? (
                        <span>{SLOT_LABELS[booking.slot] ?? `Slot ${booking.slot}`}</span>
                      ) : null}
                    </div>
                  </td>
                  <td data-label="Products">
                    <ProductLines items={booking.items} />
                  </td>
                  <td data-label="Totals">
                    <div className="tpn-bookings-totals">
                      <span>
                        <Package size={14} aria-hidden="true" />
                        {formatNumber(getBookingTotalPacks(booking))} packs
                      </span>
                      <span>{formatNumber(getBookingTotalWeight(booking), 2)} kg</span>
                    </div>
                  </td>
                  <td data-label="Transport">
                    <AssignedCarDetail booking={booking} />
                  </td>
                  <td data-label="Status">
                    <span className={`tpn-bookings-status status-${booking.status}`}>
                      {getStatusLabel(booking.status)}
                    </span>
                  </td>
                  <td data-label="เปิดโหลด">
                    <div className="tpn-bookings-actions">
                      {linkedLoads.length > 0 ? (
                        linkedLoads.map((linkedLoad) => {
                          const loadProgress = getLoadWeightProgress(
                            linkedLoad.totalWeight ?? 0,
                            linkedLoad.bookedWeight ?? 0,
                          );
                          return (
                            <button
                              key={linkedLoad.loadId}
                              type="button"
                              className={['tpn-bookings-load-card', loadProgress.isOver ? 'over' : '', linkedLoad.status === 'Completed' ? 'completed' : ''].filter(Boolean).join(' ')}
                              style={{ '--load-progress': `${loadProgress.barPercent}%` }}
                              onClick={() => openLoadDetail(linkedLoad.loadId)}
                              title={`เปิดโหลด: ${linkedLoad.name ?? linkedLoad.loadId}`}
                            >
                              <div className="tpn-bookings-load-card-id">{linkedLoad.loadId}</div>
                              <div className="tpn-bookings-load-progress-row">
                                <strong>{loadProgress.displayPercent}%</strong>
                                <div className="tpn-bookings-load-progress" aria-hidden="true">
                                  <span />
                                </div>
                              </div>
                              <div className="tpn-bookings-load-card-stats">
                                <span>{linkedLoad.requestCount ?? 0} requests</span>
                                <span>{loadProgress.weightLabel}</span>
                              </div>
                            </button>
                          );
                        })
                      ) : (
                        <>
                          <button
                            type="button"
                            className="tpn-bookings-create-load"
                            onClick={() => createLoadFromBooking(booking)}
                            disabled={!canCreateLoad || createState?.type === 'pending'}
                            title={canCreateLoad ? 'สร้างโหลดจากการจอง' : 'เฉพาะการจองที่ได้รับมอบหมายเท่านั้นที่สามารถสร้างโหลดได้'}
                          >
                            {createState?.type === 'pending' ? 'กำลังสร้างโหลด...' : 'สร้างโหลด'}
                          </button>
                          {createState?.type === 'error' && (
                            <span className="tpn-bookings-load-error" title={createState.message}>
                              {createState.message}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}

              {!isInitialLoading && filteredBookings.length === 0 && (
                <tr>
                  <td colSpan={8} className="tpn-bookings-empty">
                    ไม่มีการจองที่ตรงกับตัวกรองปัจจุบัน
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div
            ref={loadMoreSentinelRef}
            className="tpn-bookings-load-more"
            aria-hidden="true"
          />
        </div>
      </section>

      <LoadDetailModal
        detailLoadId={detailLoadId}
        detailLoad={detailLoad}
        detailLoadState={detailLoadState}
        detailLoadSummary={detailLoadSummary}
        onUpdateStatus={updateDetailLoadStatus}
        onClose={closeLoadDetail}
      />
    </main>
  );
}
